<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import conversationNotesAPI from 'dashboard/api/conversationNotes';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const { dateFnsLocale } = useDateFnsLocale();
const store = useStore();

// Use o getter de conversas
const allConversations = useMapGetter('conversations/getAllConversations');

const loading = ref(false);
const saving = ref(false);
const showForm = ref(false);
const editingNote = ref(null);
const notes = ref([]);

const form = ref({
  content: '',
  note_type: 'general',
  funnel_id: null,
  is_private: false,
  reminder_at: null,
});

// Computed para obter funis do store
const funnels = computed(() => {
  return store.getters['funnels/getFunnels'] || [];
});

// Função para obter o nome do funil pelo ID
const getFunnelName = funnelId => {
  const funnel = funnels.value.find(f => f.id === funnelId);
  return funnel ? funnel.name : '';
};

const loadingFunnels = computed(() => {
  return store.getters['funnels/isLoading'];
});

// Função para obter o funnel_id atual
const getCurrentFunnelId = () => {
  if (!allConversations.value) return null;
  const conversation = allConversations.value.find(
    c => c.id === Number(props.conversationId)
  );
  if (conversation?.funnel_mappings?.length > 0) {
    return conversation.funnel_mappings[0].funnel_id;
  }
  return null;
};

const fetchNotes = async () => {
  loading.value = true;
  try {
    const response = await conversationNotesAPI.get(props.conversationId);
    notes.value = response.data;
  } catch (error) {
    console.error('Error fetching notes:', error);
  } finally {
    loading.value = false;
  }
};

const fetchFunnels = async () => {
  try {
    await store.dispatch('funnels/get');
  } catch (error) {
    console.error('Error fetching funnels:', error);
  }
};

const resetForm = () => {
  form.value = {
    content: '',
    note_type: 'general',
    funnel_id: getCurrentFunnelId(),
    is_private: false,
    reminder_at: null,
  };
  editingNote.value = null;
};

const toggleForm = () => {
  showForm.value = !showForm.value;
  if (!showForm.value) {
    resetForm();
  }
};

const saveNote = async () => {
  if (!form.value.content.trim()) {
    useAlert(t('NOTES.VALIDATION_ERROR'));
    return;
  }

  saving.value = true;
  try {
    const noteData = {
      content: form.value.content,
      note_type: form.value.note_type,
      funnel_id: form.value.funnel_id,
      is_private: form.value.is_private,
      reminder_at: form.value.reminder_at,
    };

    if (editingNote.value) {
      const response = await conversationNotesAPI.update(
        props.conversationId,
        editingNote.value.id,
        noteData
      );
      const index = notes.value.findIndex(n => n.id === editingNote.value.id);
      if (index !== -1) {
        notes.value[index] = response.data;
      }
      useAlert(t('NOTES.API_SUCCESS.UPDATE'));
    } else {
      const response = await conversationNotesAPI.create(
        props.conversationId,
        noteData
      );
      notes.value.unshift(response.data);
      useAlert(t('NOTES.API_SUCCESS.CREATE'));
    }
    toggleForm();
  } catch (error) {
    console.error('Note save error:', error);
    useAlert(t('NOTES.API_ERROR.SAVE'));
  } finally {
    saving.value = false;
  }
};

const editNote = note => {
  editingNote.value = note;
  form.value = {
    content: note.content,
    note_type: note.note_type || 'general',
    funnel_id: note.funnel_id || getCurrentFunnelId(),
    is_private: note.is_private || false,
    reminder_at: note.reminder_at,
  };
  showForm.value = true;
};

const deleteNote = async noteId => {
  if (!confirm(t('NOTES.CONFIRM_DELETE'))) return;

  try {
    await conversationNotesAPI.delete(props.conversationId, noteId);
    notes.value = notes.value.filter(n => n.id !== noteId);
    useAlert(t('NOTES.API_SUCCESS.DELETE'));
  } catch (error) {
    useAlert(t('NOTES.API_ERROR.DELETE'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '';
  return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
};

const formatRelativeTime = dateString => {
  if (!dateString) return '';
  return formatDistanceToNow(parseISO(dateString), {
    addSuffix: true,
    locale: dateFnsLocale.value,
  });
};

const getNoteTypeLabel = type => {
  const labels = {
    general: 'Geral',
    important: 'Importante',
    follow_up: 'Acompanhamento',
    internal: 'Interna',
  };
  return labels[type] || type;
};

const getNoteTypeClass = type => {
  const classes = {
    general: 'bg-gray-100 text-gray-800',
    important: 'bg-red-100 text-red-800',
    follow_up: 'bg-yellow-100 text-yellow-800',
    internal: 'bg-purple-100 text-purple-800',
  };
  return classes[type] || 'bg-gray-100 text-gray-800';
};

onMounted(() => {
  fetchNotes();
  fetchFunnels();
});
</script>

<template>
  <div class="conversation-notes">
    <!-- Loading State -->
    <div v-if="loading" class="p-4 flex justify-center">
      <Spinner />
    </div>

    <!-- Main Content -->
    <div v-else class="p-4">
      <!-- Header with Add Button -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {{ t('NOTES.TITLE') }}
          </h4>
          <span
            v-if="notes.length > 0"
            class="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full"
          >
            {{ notes.length }}
          </span>
        </div>
        <button
          class="p-1.5 text-slate-600 hover:text-woot-600 dark:text-slate-400 dark:hover:text-woot-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
          :title="showForm ? t('COMMON.CANCEL') : 'Adicionar Nota'"
          @click="toggleForm"
        >
          <fluent-icon :icon="showForm ? 'dismiss' : 'add'" size="16" />
        </button>
      </div>

      <!-- Form -->
      <div
        v-if="showForm"
        class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-4"
      >
        <div class="space-y-3">
          <!-- Note Content Field -->
          <div>
            <label
              class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5"
            >
              Nota *
            </label>
            <textarea
              v-model="form.content"
              rows="3"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 resize-none"
              placeholder="Escreva sua nota aqui..."
            />
          </div>

          <!-- Note Type and Funnel Row -->
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5"
              >
                {{ t('NOTES.TYPE') }}
              </label>
              <select
                v-model="form.note_type"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
              >
                <option value="general">{{ t('NOTES.TYPE_GENERAL') }}</option>
                <option value="important">
                  {{ t('NOTES.TYPE_IMPORTANT') }}
                </option>
                <option value="follow_up">
                  {{ t('NOTES.TYPE_FOLLOW_UP') }}
                </option>
                <option value="internal">{{ t('NOTES.TYPE_INTERNAL') }}</option>
              </select>
            </div>
            <div>
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5"
              >
                {{ t('NOTES.FUNNEL') }}
              </label>
              <select
                v-model="form.funnel_id"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
                :disabled="loadingFunnels"
              >
                <option :value="null">{{ t('NOTES.SELECT_FUNNEL') }}</option>
                <option
                  v-for="funnel in funnels"
                  :key="funnel.id"
                  :value="funnel.id"
                >
                  {{ funnel.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Private and Reminder Row -->
          <div class="grid grid-cols-2 gap-2">
            <div class="flex items-center">
              <input
                id="private-note"
                v-model="form.is_private"
                type="checkbox"
                class="mr-2"
              />
              <label for="private-note" class="text-xs text-slate-600">
                {{ t('NOTES.PRIVATE_NOTE') }}
              </label>
            </div>
            <div>
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5"
              >
                {{ t('NOTES.REMINDER') }}
              </label>
              <input
                v-model="form.reminder_at"
                type="datetime-local"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
              />
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex justify-end gap-2 pt-2">
            <Button size="tiny" variant="clear" @click="toggleForm">
              {{ t('COMMON.CANCEL') }}
            </Button>
            <Button
              size="tiny"
              color-scheme="success"
              :loading="saving"
              @click="saveNote"
            >
              {{ editingNote ? t('COMMON.UPDATE') : t('COMMON.SAVE') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Notes List -->
      <div v-if="notes.length > 0" class="space-y-3">
        <div
          v-for="note in notes"
          :key="note.id"
          class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
          :class="
            note.is_private
              ? 'bg-yellow-50/50 dark:bg-yellow-900/10'
              : 'bg-white dark:bg-slate-900'
          "
        >
          <div class="p-3 space-y-2">
            <!-- Row 1: Agente e Timestamp -->
            <div class="flex justify-between items-start text-xs">
              <div class="flex items-center gap-2">
                <span class="text-slate-500 dark:text-slate-400 font-medium">{{ t('NOTES.AGENT') }}:</span>
                <span
                  class="text-slate-700 dark:text-slate-300 font-semibold"
                  >{{ note.user?.name || 'Sistema' }}</span>
                <span class="text-slate-400 dark:text-slate-500">• {{ formatRelativeTime(note.created_at) }}</span>
              </div>
              <!-- Action Buttons -->
              <div class="flex items-center gap-1">
                <button
                  class="p-1 text-slate-400 hover:text-woot-600 dark:hover:text-woot-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-all"
                  :title="t('COMMON.EDIT')"
                  @click="editNote(note)"
                >
                  <fluent-icon icon="edit" size="12" />
                </button>
                <button
                  class="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                  :title="t('COMMON.DELETE')"
                  @click="deleteNote(note.id)"
                >
                  <fluent-icon icon="delete" size="12" />
                </button>
              </div>
            </div>

            <!-- Row 2: Tipo e Nota Privada -->
            <div class="flex gap-4 text-xs">
              <div class="flex items-center gap-2">
                <span class="text-slate-500 dark:text-slate-400 font-medium">{{ t('NOTES.TYPE_LABEL') }}:</span>
                <span
                  :class="getNoteTypeClass(note.note_type)"
                  class="px-2 py-0.5 rounded text-xs font-medium"
                >
                  {{ getNoteTypeLabel(note.note_type) }}
                </span>
              </div>
              <div v-if="note.is_private" class="flex items-center gap-2">
                <span class="text-slate-500 dark:text-slate-400 font-medium">NOTA PRIVADA:</span>
                <span
                  class="text-yellow-700 dark:text-yellow-300 font-semibold flex items-center gap-1"
                >
                  <fluent-icon icon="lock" size="12" />
                  SIM
                </span>
              </div>
            </div>

            <!-- Row 3: Funil -->
            <div
              v-if="note.funnel_id && getFunnelName(note.funnel_id)"
              class="flex items-center gap-2 text-xs"
            >
              <span class="text-slate-500 dark:text-slate-400 font-medium">{{ t('NOTES.FUNNEL_LABEL') }}:</span>
              <span class="text-blue-700 dark:text-blue-300 font-semibold">{{
                getFunnelName(note.funnel_id)
              }}</span>
            </div>

            <!-- Row 4: Nota Content -->
            <div class="border-t border-slate-200 dark:border-slate-700 pt-2">
              <div class="flex gap-2 text-xs">
                <span class="text-slate-500 dark:text-slate-400 font-medium">{{ t('NOTES.NOTE') }}:</span>
                <p
                  class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap flex-1"
                >
                  {{ note.content }}
                </p>
              </div>
            </div>

            <!-- Row 5: Lembrete (se houver) -->
            <div
              v-if="note.reminder_at"
              class="border-t border-slate-200 dark:border-slate-700 pt-2"
            >
              <div class="flex items-center gap-2 text-xs">
                <span class="text-slate-500 dark:text-slate-400 font-medium">LEMBRETE:</span>
                <span
                  class="text-orange-600 dark:text-orange-400 font-semibold flex items-center gap-1"
                >
                  <fluent-icon icon="alarm" size="12" />
                  {{ formatDate(note.reminder_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!showForm" class="text-center py-8">
        <fluent-icon
          icon="book"
          size="32"
          class="text-slate-300 dark:text-slate-600 mb-2"
        />
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Nenhuma nota adicionada ainda
        </p>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
          Clique no botão + para adicionar uma nota
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversation-notes {
  // Sem fundo branco, usando o padrão do Chatwoot
}
</style>
